import { useQuery } from '@tanstack/react-query';
import { statsApi, DashboardStats, GroomingCancellationByOwner } from '@/services/stats';

interface DateRange {
  startDate?: Date;
  endDate?: Date;
}

function formatDateParams(dateRange?: DateRange) {
  if (!dateRange) return {};
  return {
    start_date: dateRange.startDate?.toISOString().split('T')[0],
    end_date: dateRange.endDate?.toISOString().split('T')[0],
  };
}

export function useDashboardStats(dateRange?: DateRange) {
  return useQuery<DashboardStats>({
    queryKey: ['stats', 'dashboard', dateRange],
    queryFn: () => statsApi.getDashboardStats(formatDateParams(dateRange)),
  });
}

export function useSalesSummary(dateRange?: DateRange) {
  return useQuery({
    queryKey: ['stats', 'sales', 'summary', dateRange],
    queryFn: () => statsApi.getSalesSummary(formatDateParams(dateRange)),
  });
}

export function useSalesByPeriod(dateRange?: DateRange, groupBy: 'day' | 'week' | 'month' = 'day') {
  return useQuery({
    queryKey: ['stats', 'sales', 'by-period', dateRange, groupBy],
    queryFn: () => statsApi.getSalesByPeriod({ ...formatDateParams(dateRange), group_by: groupBy }),
  });
}

export function useSalesByPaymentMethod(dateRange?: DateRange) {
  return useQuery({
    queryKey: ['stats', 'sales', 'by-payment-method', dateRange],
    queryFn: () => statsApi.getSalesByPaymentMethod(formatDateParams(dateRange)),
  });
}

export function useTopSellingProducts(dateRange?: DateRange, limit: number = 10) {
  return useQuery({
    queryKey: ['stats', 'products', 'top-selling', dateRange, limit],
    queryFn: () => statsApi.getTopSellingProducts({ ...formatDateParams(dateRange), limit }),
  });
}

export function useSalesByCategory(dateRange?: DateRange) {
  return useQuery({
    queryKey: ['stats', 'products', 'by-category', dateRange],
    queryFn: () => statsApi.getSalesByCategory(formatDateParams(dateRange)),
  });
}

export function useLowStockProducts(limit: number = 20) {
  return useQuery({
    queryKey: ['stats', 'products', 'low-stock', limit],
    queryFn: () => statsApi.getLowStockProducts(limit),
  });
}

export function useNewPatientsByPeriod(dateRange?: DateRange, groupBy: 'day' | 'week' | 'month' = 'month') {
  return useQuery({
    queryKey: ['stats', 'patients', 'new', dateRange, groupBy],
    queryFn: () => statsApi.getNewPatientsByPeriod({ ...formatDateParams(dateRange), group_by: groupBy }),
  });
}

export function usePatientsBySpecies() {
  return useQuery({
    queryKey: ['stats', 'patients', 'by-species'],
    queryFn: () => statsApi.getPatientsBySpecies(),
  });
}

export function useServicesSummary(dateRange?: DateRange) {
  return useQuery({
    queryKey: ['stats', 'services', 'summary', dateRange],
    queryFn: () => statsApi.getServicesSummary(formatDateParams(dateRange)),
  });
}

export function useGroomingCancellationsByOwner(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {};
  return useQuery<GroomingCancellationByOwner[]>({
    queryKey: ['stats', 'grooming', 'cancellations-by-owner'],
    queryFn: () => statsApi.getGroomingCancellationsByOwner(),
    enabled,
  });
}
